/**
 * multiSiteGeoMap — the ALL-SITES alert board's GeoMapApi (its Map View).
 *
 * Unlike a floor plan, the geographic map is ONE canvas the whole estate fits on — there is one
 * Earth — so this adapter just unions every site's camera coordinates onto it. Same contract as
 * the selected-node adapter (geoMap.adapter); the shared board needs no notion of sites.
 *
 * ── Camera ids are SITE-QUALIFIED ──────────────────────────────────────────────────────────
 * `nodeId::cameraId`, matching createMultiSiteHexAlertsApi: the board scopes its alert panel and
 * live tiles by matching placement camera ids against that adapter's feed, so a bare id here
 * would leave every pin dark. The site name is folded into `camera_location` (the pin's hover
 * note), since two sites can each have a "Gate 1".
 *
 * ── Which config ───────────────────────────────────────────────────────────────────────────
 * The config does two jobs and they are answered separately:
 *   · BASEMAP (tile_url / attribution / geocoder) — the first site that answers with a SAVED
 *     config, else the first that answers at all, in roster order. Tiles are fetched by the
 *     operator's browser, never through the tunnel: offline PMTiles installed on the cloud's own
 *     origin win regardless (useBasemap), otherwise this one site's tile_url is used for the
 *     whole estate. Mixing per-site tile servers on one map is not something MapLibre does.
 *   · OPENING VIEW (centre / zoom) — the board opens on the config's centre and only frames the
 *     pins when "Fit" is pressed, so one site's saved view would open the estate map on that
 *     site alone. When every placed camera belongs to that one site its saved view is kept;
 *     otherwise the view is derived from the bounding box of all placements.
 *
 * ── Read-only ──────────────────────────────────────────────────────────────────────────────
 * Saving a default view or moving a pin is a site's business; the page passes
 * `canEdit={false}` and both writes reject.
 *
 * ── Offline sites ──────────────────────────────────────────────────────────────────────────
 * allSettled throughout: a site that doesn't answer contributes no pins. Only when EVERY site
 * fails does a call reject, so the board says "could not load" instead of drawing an empty map
 * as if no camera had coordinates.
 */
import type { GeoMapApi, GeoMapConfig, GeoPlacement } from '@trinetra/shared';
import { getNodeGeoMapConfig, getNodeGeoPlacements } from '../services/api.service';
import { siteCameraKey, splitSiteCameraKey, type SiteRef } from './multiSiteHexAlerts.adapter';

const CROSS_SITE_READ_ONLY =
  'The map cannot be edited across sites — switch to that site to place cameras or save its default view.';

const readOnly = (): Promise<never> => Promise.reject(new Error(CROSS_SITE_READ_ONLY));

/** Re-throw a proxy failure with the NODE's message (the cloud BE relays it verbatim). */
function rethrow(e: any): never {
  const body = e?.response?.data;
  throw new Error(body?.message || body?.error || e?.message || 'Request failed');
}

/** Reject with the first site's error when no site answered at all. */
function allFailed(settled: PromiseSettledResult<unknown>[]): never {
  const first = settled.find((r): r is PromiseRejectedResult => r.status === 'rejected');
  rethrow(first?.reason);
}

// Assumed board size when deriving a zoom from a bounding box. Conservative on purpose — the
// view only has to contain the estate on open; "Fit" frames it exactly.
const VIEW_W_PX = 800;
const VIEW_H_PX = 500;
const TILE_PX = 256; // GeoMapConfig.zoom is the classic 256-px-tile scale
const MIN_ZOOM = 2;
const MAX_ZOOM = 16;

const mercY = (lat: number) => {
  const r = (Math.max(-85, Math.min(85, lat)) * Math.PI) / 180;
  return Math.log(Math.tan(Math.PI / 4 + r / 2));
};

/** Centre + zoom that contains every placement. */
function viewFor(rows: GeoPlacement[]): Pick<GeoMapConfig, 'center_lat' | 'center_lng' | 'zoom'> {
  const lats = rows.map((r) => r.lat);
  const lngs = rows.map((r) => r.lng);
  const [s, n] = [Math.min(...lats), Math.max(...lats)];
  const [w, e] = [Math.min(...lngs), Math.max(...lngs)];
  const lngFrac = (e - w) / 360;
  const latFrac = (mercY(n) - mercY(s)) / (2 * Math.PI);
  const zFor = (frac: number, px: number) => (frac > 0 ? Math.log2(px / (TILE_PX * frac)) : MAX_ZOOM);
  const zoom = Math.floor(Math.min(zFor(lngFrac, VIEW_W_PX), zFor(latFrac, VIEW_H_PX)));
  return {
    center_lat: (s + n) / 2,
    center_lng: (w + e) / 2,
    zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)),
  };
}

/**
 * Build the all-sites GeoMapApi over `sites`.
 *
 * A NEW object per call and the board reloads when the api identity changes, so callers must
 * memoize it on the site roster (AllSitesAlertBoardPage keys on the node ids).
 */
export function createMultiSiteGeoMapApi(sites: SiteRef[]): GeoMapApi {
  // The board asks for config() and placements() together, and config() needs the placements
  // to frame the estate — so the two share one fan-out while it is in flight.
  let inflight: Promise<GeoPlacement[]> | null = null;

  const loadPlacements = (): Promise<GeoPlacement[]> => {
    if (inflight) return inflight;
    const run = (async () => {
      if (sites.length === 0) return [];
      const settled = await Promise.allSettled(
        sites.map(async (site) => {
          const rows: any = await getNodeGeoPlacements(site.nodeId);
          const list: GeoPlacement[] = Array.isArray(rows) ? rows : [];
          return list.map((p) => ({
            ...p,
            camera_id: siteCameraKey(site.nodeId, p.camera_id),
            camera_location: [site.name, p.camera_location].filter(Boolean).join(' · ') || null,
          }));
        }),
      );
      if (!settled.some((r) => r.status === 'fulfilled')) allFailed(settled);
      return settled.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
    })();
    inflight = run;
    run.then(
      () => { if (inflight === run) inflight = null; },
      () => { if (inflight === run) inflight = null; },
    );
    return run;
  };

  return {
    async config(): Promise<GeoMapConfig> {
      if (sites.length === 0) throw new Error('No sites to show');
      const [settled, rows] = await Promise.all([
        Promise.allSettled(
          sites.map(async (site) => ({ nodeId: site.nodeId, cfg: (await getNodeGeoMapConfig(site.nodeId)) as GeoMapConfig })),
        ),
        loadPlacements().catch(() => [] as GeoPlacement[]),
      ]);
      const ok = settled.flatMap((r) => (r.status === 'fulfilled' && r.value.cfg ? [r.value] : []));
      if (ok.length === 0) allFailed(settled);

      // Basemap: the first site with a deliberately saved config, else the first that answered.
      const base = ok.find((s) => s.cfg.is_configured) || ok[0];

      // Opening view: keep the base site's saved view when it already contains every pin
      // (they are all its cameras); otherwise frame the estate.
      const placedSites = new Set(rows.map((r) => splitSiteCameraKey(r.camera_id).nodeId));
      const ownView =
        base.cfg.is_configured && (rows.length === 0 || (placedSites.size === 1 && placedSites.has(base.nodeId)));
      if (ownView || rows.length === 0) return base.cfg;
      return { ...base.cfg, ...viewFor(rows), is_configured: true };
    },

    placements: loadPlacements,

    saveConfig: readOnly,
    savePlacements: readOnly,
  };
}
